import React from 'react';
import { Check } from 'lucide-react';

import { cn } from 'shared/lib/utils';

interface OwnerContactToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint: string;
}

/**
 * Full-width clickable card used on the representative step to mark the
 * registrant as the primary contact. Designed to read as an interactive
 * control (hover, focus ring, circular check) rather than a plain checkbox.
 */
const OwnerContactToggle: React.FC<OwnerContactToggleProps> = ({
  checked,
  onChange,
  label,
  hint,
}) => {
  const toggle = () => onChange(!checked);

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          toggle();
        }
      }}
      className={cn(
        'w-full text-start rounded-2xl border-2 p-4 flex items-start gap-3 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-haat-red-500 focus-visible:ring-offset-2',
        checked
          ? 'border-haat-red-500 bg-haat-red-50/60 shadow-sm'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50',
      )}
    >
      <span
        className={cn(
          'mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors',
          checked
            ? 'border-haat-red-600 bg-haat-red-600 text-white'
            : 'border-gray-300 bg-white text-transparent',
        )}
        aria-hidden="true"
      >
        <Check size={14} strokeWidth={3} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-gray-900">{label}</span>
        <span className="block text-xs text-gray-600 leading-relaxed mt-0.5">{hint}</span>
      </span>
    </button>
  );
};

export default OwnerContactToggle;
